/**
 * Utilitaire pour nettoyer les données du localStorage qui pourraient être corrompues
 */

/**
 * Nettoie une chaîne JSON qui pourrait contenir des caractères d'échappement invalides
 */
export const sanitizeJsonString = (jsonString: string): string => {
  if (!jsonString) return '';
  
  // Remplacer les caractères nuls et autres caractères de contrôle qui peuvent causer des erreurs
  return jsonString
    .replace(/\\u0000/g, '') // Caractère nul Unicode
    .replace(/\0/g, '')      // Caractère nul ASCII
    .replace(/\\([^"\\/bfnrtu])/g, '\\\\$1'); // Échapper les caractères d'échappement invalides
};

/**
 * Récupère un objet du localStorage de manière sécurisée
 */
export const safeGetItem = <T>(key: string, defaultValue: T): T => {
  if (typeof window === 'undefined') return defaultValue;
  
  try {
    const item = localStorage.getItem(key);
    if (!item) return defaultValue;
    
    // Nettoyer la chaîne JSON avant de la parser
    const sanitizedItem = sanitizeJsonString(item);
    return JSON.parse(sanitizedItem);
  } catch (error) {
    console.error(`🔐 Erreur lors de la récupération de ${key} depuis localStorage:`, error);
    
    // En cas d'erreur, supprimer l'élément corrompu
    try {
      localStorage.removeItem(key);
      console.log(`🧹 Élément corrompu ${key} supprimé du localStorage`);
    } catch (removeError) {
      console.error(`❌ Impossible de supprimer l'élément corrompu ${key}:`, removeError);
    }
    
    return defaultValue;
  }
};

/**
 * Stocke un objet dans le localStorage de manière sécurisée
 */
export const safeSetItem = <T>(key: string, value: T): void => {
  if (typeof window === 'undefined') return;
  
  try {
    const jsonValue = JSON.stringify(value);
    localStorage.setItem(key, jsonValue);
  } catch (error) {
    console.error(`❌ Erreur lors du stockage de ${key} dans localStorage:`, error);
  }
};

/**
 * Nettoie les entrées du localStorage connues pour être problématiques
 */
export const cleanLocalStorage = (): void => {
  if (typeof window === 'undefined') return;
  
  const keysToClean = [
    'user_profile',
    'access_token',
    'refresh_token',
    'user_data'
  ];
  
  keysToClean.forEach(key => {
    try {
      const item = localStorage.getItem(key);
      if (!item) return;
      
      // Tenter de parser l'élément pour vérifier s'il est valide
      try {
        JSON.parse(item);
      } catch (parseError) {
        console.warn(`🧹 Nettoyage de l'élément corrompu ${key}`);
        // Si le parsing échoue, essayer de le nettoyer
        const sanitizedItem = sanitizeJsonString(item);
        try {
          JSON.parse(sanitizedItem);
          // Si le parsing réussit après nettoyage, stocker la version nettoyée
          localStorage.setItem(key, sanitizedItem);
          console.log(`✅ Élément ${key} nettoyé et restauré`);
        } catch (secondParseError) {
          // Si ça échoue toujours, supprimer l'élément
          localStorage.removeItem(key);
          console.log(`🗑️ Élément ${key} supprimé car impossible à réparer`);
        }
      }
    } catch (error) {
      console.error(`❌ Erreur lors du nettoyage de ${key}:`, error);
    }
  });
  
  console.log('🧹 Nettoyage du localStorage terminé');
};

// Exporter une fonction pour nettoyer le localStorage au démarrage de l'application
export const initLocalStorageCleaner = (): void => {
  if (typeof window === 'undefined') return;
  
  // Attendre que le DOM soit chargé
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', cleanLocalStorage);
  } else {
    cleanLocalStorage();
  }
};
